/*=========================================================================================
  File Name: googleIdentity.ts
  Description: Integracao com o Google Identity Services (login com o Google).

  O Google entrega no browser um ID token (JWT assinado) que o backend verifica em
  POST /auth/social/google. Nao existe popup de OAuth, redirect nem pagina de callback:
  o unico artefato que atravessa a fronteira e o token, e ele nunca e persistido.

  O script e carregado sob demanda — so as telas de autenticacao precisam dele — e uma
  unica vez por sessao de navegacao.

  Sem VUE_APP_GOOGLE_CLIENT_ID tudo vira no-op e o botao nao aparece, do mesmo jeito que
  o reCAPTCHA se desliga sem a site key. E o que mantem o ambiente local funcionando para
  quem nao configurou o client OAuth.

  Este arquivo e gemeo do que existe no marketplace. Sao dois clients OAuth distintos
  (um por SPA, para poder revogar um sem derrubar o outro), e e o `aud` do token que diz
  ao backend de qual app veio — e dai que sai a origem do consentimento registrado.
  Conta com papel `admin` nao entra por aqui: o backend recusa, de proposito.
==========================================================================================*/

const CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID || ''
const SCRIPT_ID = 'google-identity-services'
const SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

// O botao do Google nao acompanha a largura do container: `renderButton` exige um numero
// e o Google limita a 400px. Medimos o container e respeitamos esse teto.
const MIN_BUTTON_WIDTH = 200
const MAX_BUTTON_WIDTH = 400

let loader: Promise<any> | null = null
let initialized = false

/**
 * `google.accounts.id.initialize` e global e so aceita UM callback. Guardamos o handler
 * corrente aqui e inicializamos uma unica vez com um despachante estavel — reinicializar
 * a cada montagem de botao faria o Google recriar estado interno sem necessidade.
 */
let currentHandler: ((idToken: string) => void) | null = null

export function isGoogleSignInEnabled(): boolean {
  return CLIENT_ID !== ''
}

function handleCredential(response: any): void {
  if (!response || !response.credential) return
  if (currentHandler) currentHandler(response.credential)
}

function loadScript(): Promise<any> {
  if (loader) return loader

  loader = new Promise((resolve, reject) => {
    const win = window as any

    // Script ja presente (troca de rota, hot reload) — reaproveita.
    if (win.google && win.google.accounts && win.google.accounts.id) {
      resolve(win.google)
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true

    script.onload = () => {
      const google = (window as any).google
      if (!google || !google.accounts || !google.accounts.id) {
        loader = null
        reject(new Error('google.accounts.id indisponivel apos o carregamento do script'))
        return
      }
      resolve(google)
    }

    script.onerror = () => {
      // Zera o cache para permitir nova tentativa (adblock, rede instavel).
      loader = null
      script.remove()
      reject(new Error('falha ao carregar o script do Google Identity Services'))
    }

    document.head.appendChild(script)
  })

  return loader
}

function resolveWidth(container: HTMLElement): number {
  const measured = Math.round(container.getBoundingClientRect().width)
  if (!measured) return MAX_BUTTON_WIDTH
  return Math.min(MAX_BUTTON_WIDTH, Math.max(MIN_BUTTON_WIDTH, measured))
}

/**
 * Renderiza o botao oficial do Google dentro do container.
 *
 * O botao e do Google por exigencia dele: as diretrizes de marca nao permitem um botao
 * proprio disparando o fluxo. `handler` recebe o ID token cru.
 *
 * Rejeita quando o script nao carrega — quem chama decide o que mostrar no lugar.
 */
export function renderGoogleButton(
  container: HTMLElement,
  handler: (idToken: string) => void,
  options: { text?: string } = {}
): Promise<void> {
  if (!isGoogleSignInEnabled()) {
    return Promise.reject(new Error('VUE_APP_GOOGLE_CLIENT_ID nao configurada'))
  }

  return loadScript().then((google: any) => {
    currentHandler = handler

    if (!initialized) {
      google.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: handleCredential,
        // One Tap fica de fora: exige tratar FedCM e cookies de terceiros, e nao e
        // preciso para o botao funcionar. `auto_select` desligado evita que uma sessao
        // Google antiga autentique alguem sem intencao explicita.
        auto_select: false,
        cancel_on_tap_outside: true,
        ux_mode: 'popup'
      })
      initialized = true
    }

    // Renderizacoes anteriores (troca de rota) deixam o iframe antigo para tras.
    container.innerHTML = ''

    google.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      shape: 'pill',
      text: options.text || 'continue_with',
      logo_alignment: 'left',
      locale: 'pt-BR',
      width: resolveWidth(container)
    })
  })
}

/**
 * Solta o handler ao desmontar a tela. Sem isso, um botao remanescente de outra rota
 * dispararia o fluxo do componente ja destruido.
 */
export function releaseGoogleButton(handler: (idToken: string) => void): void {
  if (currentHandler === handler) currentHandler = null
}

export default {
  isGoogleSignInEnabled,
  renderGoogleButton,
  releaseGoogleButton
}
